import { useState, useEffect, useCallback } from 'react';
import api from '../../hooks/useApi';
import { formatMoney } from '../../utils/format';
import { useToast } from '../ui/Toast';
import SowSelect from '../shared/SowSelect';

// PMO-managed review queue for order confirmations forwarded to the ad-hoc
// purchases mailbox (see syncAdhocPurchaseEmails). Every captured email is
// triaged from here — allocate to a SOW/overhead, raise a PO, or ignore —
// rather than routed to whichever person the sender happened to resolve to;
// in practice most of what lands in this mailbox is supplier marketing, not
// a genuine employee forward, so a human reviews every item centrally.
export default function AdhocPurchasesReviewPage() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [sows, setSows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const load = useCallback(() => {
    api.get('procurement/adhoc-purchases').then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);
  useEffect(() => {
    Promise.all([
      api.get('sows').catch(() => []),
      api.get('invoice-overhead-categories').catch(() => []),
      api.get('suppliers').catch(() => []),
    ]).then(([s, c, sup]) => { setSows(s); setCategories(c); setSuppliers(sup); });
  }, []);

  const activeSows = sows.filter(s => !['closed', 'cancelled'].includes(s.status));

  const allocate = async (invoiceId, data) => {
    await api.patch(`procurement/supplier-invoices/${invoiceId}/allocate`, data);
    toast.success('Allocated' + (data.raise_po ? ' — draft PO raised' : ''));
    setExpanded(null);
    load();
  };

  const ignore = async (invoiceId) => {
    await api.patch(`procurement/supplier-invoices/${invoiceId}/ignore`, {});
    toast.success('Ignored');
    setExpanded(null);
    load();
  };

  return (
    <div className="fade-in">
      <div className="mb-3">
        <h2 className="text-base font-extrabold text-navy">Ad-hoc Purchases</h2>
        <div className="text-[9px] text-gray-500">
          Order confirmations forwarded to the ad-hoc purchases mailbox. Review each one — allocate to a SOW or overhead category, raise a PO, or ignore.
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded overflow-hidden">
        <div className="flex items-center gap-3 px-3 py-1.5 bg-gray-50 border-b border-gray-100 text-[8px] uppercase text-gray-500 font-bold">
          <div className="flex-1">Sender / Subject</div>
          <div className="w-28">Supplier</div>
          <div className="w-20 text-right">Value</div>
          <div className="w-16 text-right">Received</div>
          <div className="w-4" />
        </div>
        {items.map(item => (
          <Row key={item.email_id} item={item} expanded={expanded === item.email_id}
            onToggle={() => setExpanded(expanded === item.email_id ? null : item.email_id)}
            sows={activeSows} categories={categories} suppliers={suppliers}
            onAllocate={allocate} onIgnore={ignore} />
        ))}
        {!loading && items.length === 0 && (
          <div className="text-center py-8 text-[10px] text-gray-400">🎉 Nothing waiting to be reviewed.</div>
        )}
      </div>
    </div>
  );
}

function Row({ item, expanded, onToggle, sows, categories, suppliers, onAllocate, onIgnore }) {
  const [sowId, setSowId] = useState(item.sow_id || '');
  const [categoryId, setCategoryId] = useState(item.overhead_category_id || '');
  const [supplierId, setSupplierId] = useState('');
  const [raisePo, setRaisePo] = useState(false);
  const [costType, setCostType] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [rechargeable, setRechargeable] = useState(false);
  const [pmoNote, setPmoNote] = useState('');
  const [previewAttId, setPreviewAttId] = useState((item.attachments || [])[0]?.id || null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    await onAllocate(item.invoice_id, {
      sow_id: sowId || null,
      overhead_category_id: categoryId || null,
      supplier_id: supplierId || null,
      raise_po: raisePo,
      cost_type: costType || null,
      payment_method: paymentMethod || null,
      rechargeable,
      pmo_note: pmoNote.trim() || null,
    }).catch(() => setBusy(false));
  };

  return (
    <div className="border-b border-gray-100 last:border-0">
      <div onClick={onToggle} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gold/5">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-semibold text-navy truncate">{item.sender_name || item.sender_email}</span>
            {item.matched_person_name && (
              <span className="text-[7px] font-bold text-emerald-700 bg-emerald-50 px-1 py-0.5 rounded flex-shrink-0" title="Sender's email matched this person — informational only, not assigned to them">
                matched: {item.matched_person_name}
              </span>
            )}
          </div>
          <div className="text-[9px] text-gray-500 truncate">{item.subject}</div>
        </div>
        <div className="w-28 text-[9px] text-gray-600 truncate">{item.supplier_name || '—'}</div>
        <div className="w-20 text-[10px] font-bold text-right">{item.total_incl_vat != null ? formatMoney(item.total_incl_vat) : '—'}</div>
        <div className="w-16 text-[8px] text-gray-400 text-right">{item.received_at ? new Date(item.received_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : ''}</div>
        <div className="w-4 text-gray-400 text-[9px] text-center">{expanded ? '▲' : '▼'}</div>
      </div>

      {expanded && (
        <div className="px-3 pb-3 bg-gray-50/50 border-t border-gray-100" onClick={e => e.stopPropagation()}>
          <div className="grid grid-cols-2 gap-4 pt-3">
            {/* Left: extracted info + attachment preview */}
            <div>
              <div className="text-[9px] text-gray-600 space-y-1 mb-2">
                <div><span className="font-bold text-gray-500">From:</span> {item.sender_email}</div>
                {item.invoice_number && <div><span className="font-bold text-gray-500">Order ref:</span> {item.invoice_number}</div>}
                {item.notes && <div><span className="font-bold text-gray-500">AI extract:</span> {item.notes}</div>}
              </div>
              {(item.attachments || []).length > 0 ? (
                <div>
                  <div className="flex gap-1 flex-wrap mb-1">
                    {item.attachments.map(a => (
                      <button key={a.id} onClick={() => setPreviewAttId(a.id)}
                        className={`text-[8px] px-1.5 py-0.5 rounded border truncate max-w-[140px] ${previewAttId === a.id ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                        title={a.filename}>
                        {a.filename}
                      </button>
                    ))}
                  </div>
                  {previewAttId && (
                    <iframe
                      src={`/api/procurement/attachments/${previewAttId}/download`}
                      className="w-full h-[380px] border border-gray-200 rounded bg-white"
                      title="Attachment preview"
                    />
                  )}
                </div>
              ) : (
                <div className="text-[9px] text-gray-400 italic">No attachment.</div>
              )}
            </div>

            {/* Right: allocate / ignore */}
            <div className="space-y-2">
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Bill to a SOW</label>
                <SowSelect sows={sows} value={sowId} onChange={setSowId} placeholder="— Select SOW —" />
              </div>
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Or overhead category</label>
                <select className="form-select" value={categoryId} onChange={e => setCategoryId(e.target.value)}>
                  <option value="">— None —</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Supplier <span className="font-normal text-gray-400">(if known)</span></label>
                <select className="form-select" value={supplierId} onChange={e => setSupplierId(e.target.value)}>
                  <option value="">— Not specified —</option>
                  {suppliers.filter(s => s.is_active !== 0).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Cost type</label>
                  <select className="form-select" value={costType} onChange={e => setCostType(e.target.value)}>
                    <option value="">— Select —</option>
                    {['Hardware', 'Consumable', 'Software / licence', 'Service', 'Travel', 'Other'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Payment</label>
                  <select className="form-select" value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                    <option value="">— Select —</option>
                    <option value="company">Company account</option>
                    <option value="personal">Personal card (reimburse)</option>
                    <option value="unpaid">Not yet paid</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Note <span className="font-normal text-gray-400">(what it's for)</span></label>
                <input className="form-input" value={pmoNote} onChange={e => setPmoNote(e.target.value)} placeholder="e.g. replacement PSU for the D154 rack" />
              </div>
              <label className="flex items-center gap-1.5 text-[9px] text-gray-600 cursor-pointer">
                <input type="checkbox" checked={rechargeable} onChange={e => setRechargeable(e.target.checked)} className="w-3 h-3" />
                Rechargeable to the client (vs absorbed cost)
              </label>
              <label className="flex items-center gap-1.5 text-[9px] text-gray-600 cursor-pointer">
                <input type="checkbox" checked={raisePo} onChange={e => setRaisePo(e.target.checked)} className="w-3 h-3" />
                Raise a purchase order (retrospective — already purchased)
              </label>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={submit}
                  disabled={busy || (!sowId && !categoryId && !raisePo)}
                  className="px-3 py-1 text-[9px] font-semibold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-40"
                >
                  {busy ? 'Saving...' : 'Allocate'}
                </button>
                <button
                  onClick={() => onIgnore(item.invoice_id)}
                  className="px-3 py-1 text-[9px] font-semibold border border-red-300 text-red-600 rounded hover:bg-red-50"
                >
                  Ignore
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
